import { ACTIVE_LOCATION_STORAGE_KEY } from '@jq4race/shared';

export interface SwitchActiveLocationDeps {
  /** Injectable for tests. Defaults to `() => window.location.reload()`. */
  reload?: () => void;
}

/**
 * Applies a supervisor's location switch: persists `nextId` and forces a
 * full page reload.
 *
 * Why reload and not a reactive store/context: `localStorage` is read in at
 * least two places that are NOT reactive — `SupervisorDashboardPage` reads
 * the key directly during render, and `@jq4race/api-client`'s `client.ts`
 * reads it on every request to set `X-Location-Id`. Writing the new value
 * alone would leave both pointed at the old location until something else
 * happened to trigger a refetch — which would silently show the wrong
 * location's data. A full reload guarantees every query, header injection
 * and render re-reads the key, with no stale state left anywhere.
 *
 * Known trade-off, accepted deliberately: the switcher lives in the topbar,
 * so it is reachable from every supervisor screen — and supervisors DO get
 * form-bearing screens (they may manage events: EventForm, AddGuestForm,
 * BulkImportGuestsForm). Switching while one of those is half-filled loses
 * the input, silently. It is accepted because switching takes two
 * deliberate steps (open the picker, then pick a DIFFERENT location) and
 * because the alternative — a reactive store plus query invalidation —
 * would have to reach into every non-reactive read listed above to avoid
 * showing one location's data under another location's name, which is a
 * far worse failure. Revisit if supervisors report losing form work.
 *
 * No-ops (no write, no reload) when `nextId` already matches `currentId` —
 * re-selecting the current location must not disrupt the page.
 */
export function switchActiveLocation(
  nextId: string,
  currentId: string | null,
  deps: SwitchActiveLocationDeps = {},
): boolean {
  if (nextId === currentId) return false;

  window.localStorage.setItem(ACTIVE_LOCATION_STORAGE_KEY, nextId);
  const reload = deps.reload ?? (() => window.location.reload());
  reload();
  return true;
}
